import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormikErrors, FormikTouched } from 'formik';

import {
  ADDRESS_SUFFIXES,
  IDuplicateFormValues,
} from '../schemas/duplicateShipmentFormSchema';

interface IUseDuplicateSnapshotHandlersParams {
  values: IDuplicateFormValues;
  errors: FormikErrors<IDuplicateFormValues>;
  touched: FormikTouched<IDuplicateFormValues>;
  setValues: (fn: (prev: IDuplicateFormValues) => IDuplicateFormValues) => void;
  setTouched: (t: FormikTouched<IDuplicateFormValues>) => void;
  handlePartnerChange: (id: string) => void;
  setFieldValue: (field: string, value: unknown) => void;
  file: File | null;
  returnFile: File | null;
  setFile: (f: File | null) => void;
  setReturnFile: (f: File | null) => void;
}

interface ISnapshotHandlers {
  onEditStart: () => void;
  onCancel: () => void;
}

export const useDuplicateSnapshotHandlers = ({
  values,
  errors,
  touched,
  setValues,
  setTouched,
  handlePartnerChange,
  setFieldValue,
  file,
  returnFile,
  setFile,
  setReturnFile,
}: IUseDuplicateSnapshotHandlersParams) => {
  const [editingCardsCount, setEditingCardsCount] = useState(0);
  const snapshotsRef = useRef<Map<string, Record<string, unknown>>>(new Map());
  const fileSnapshotsRef = useRef<Map<string, File | null>>(new Map());
  const valuesRef = useRef(values);
  const fileRef = useRef(file);
  const returnFileRef = useRef(returnFile);

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  useEffect(() => {
    fileRef.current = file;
  }, [file]);

  useEffect(() => {
    returnFileRef.current = returnFile;
  }, [returnFile]);

  const handleEditingChange = useCallback((editing: boolean) => {
    setEditingCardsCount((prev) =>
      editing ? prev + 1 : Math.max(0, prev - 1)
    );
  }, []);

  const hasErrorsInFields = useCallback(
    (fields: string[]) =>
      fields.some((field) => {
        const hasError = !!(errors as Record<string, string>)[field];
        const isTouched = !!(touched as Record<string, boolean>)[field];

        return hasError && isTouched;
      }),
    [errors, touched]
  );

  const createSnapshotHandlers = useCallback(
    (
      cardKey: string,
      fields: Array<keyof IDuplicateFormValues>
    ): ISnapshotHandlers => ({
      onEditStart: () => {
        const snapshot: Record<string, unknown> = {};
        const currentValues = valuesRef.current;
        fields.forEach((field) => {
          snapshot[field] = currentValues[field];
        });
        snapshotsRef.current.set(cardKey, snapshot);
      },
      onCancel: () => {
        const snapshot = snapshotsRef.current.get(cardKey);
        if (snapshot) {
          setValues((prev) => ({ ...prev, ...snapshot }));

          const touchedReset = fields.reduce(
            (acc, field) => {
              acc[field] = false;

              return acc;
            },
            {} as Record<string, boolean>
          );
          setTouched({ ...touched, ...touchedReset });

          snapshotsRef.current.delete(cardKey);
        }
      },
    }),
    [setValues, setTouched, touched]
  );

  const getAddressFields = useCallback(
    (prefix: string) =>
      ADDRESS_SUFFIXES.map((suffix) => `${prefix}${suffix}`) as Array<
        keyof IDuplicateFormValues
      >,
    []
  );

  const partnershipHandlers = useMemo(
    (): ISnapshotHandlers => ({
      onEditStart: () => {
        const snapshot: Record<string, unknown> = {};
        const currentValues = valuesRef.current;
        (['partnerId', 'selectedPartner'] as const).forEach((field) => {
          snapshot[field] = currentValues[field];
        });
        snapshotsRef.current.set('partnership', snapshot);
      },
      onCancel: () => {
        const snapshot = snapshotsRef.current.get('partnership');
        if (snapshot) {
          const restoredPartnerId = snapshot.partnerId as string;
          handlePartnerChange(restoredPartnerId);
          setFieldValue('selectedPartner', snapshot.selectedPartner);

          setTouched({
            ...touched,
            partnerId: false,
            selectedPartner: false,
          });

          snapshotsRef.current.delete('partnership');
        }
      },
    }),
    [handlePartnerChange, setFieldValue, setTouched, touched]
  );

  const boxHandlers = createSnapshotHandlers('box', ['boxId', 'boxLabel']);

  const providerStationHandlers = createSnapshotHandlers('providerStation', [
    'providerStationId',
  ]);

  const typeHandlers = createSnapshotHandlers('shipmentType', ['type']);

  const parcelHandlers = useMemo(
    (): ISnapshotHandlers => ({
      onEditStart: () => {
        const snapshot: Record<string, unknown> = {};
        const currentValues = valuesRef.current;
        (
          [
            'carrier',
            'weightInGrams',
            'carrierTrackingCode',
            'reference',
            'serviceType',
          ] as const
        ).forEach((field) => {
          snapshot[field] = currentValues[field];
        });
        snapshotsRef.current.set('parcel', snapshot);
        fileSnapshotsRef.current.set('parcel', fileRef.current);
      },
      onCancel: () => {
        const snapshot = snapshotsRef.current.get('parcel');
        if (snapshot) {
          setValues((prev) => ({ ...prev, ...snapshot }));

          const fields = [
            'carrier',
            'weightInGrams',
            'carrierTrackingCode',
            'reference',
            'serviceType',
          ];
          const touchedReset = fields.reduce(
            (acc, field) => {
              acc[field] = false;

              return acc;
            },
            {} as Record<string, boolean>
          );
          setTouched({ ...touched, ...touchedReset });

          snapshotsRef.current.delete('parcel');
        }
        if (fileSnapshotsRef.current.has('parcel')) {
          setFile(fileSnapshotsRef.current.get('parcel') || null);
          fileSnapshotsRef.current.delete('parcel');
        }
      },
    }),
    [setValues, setTouched, setFile, touched]
  );

  const senderHandlers = createSnapshotHandlers(
    'sender',
    getAddressFields('sender')
  );

  const recipientHandlers = createSnapshotHandlers(
    'recipient',
    getAddressFields('recipient')
  );

  const returnCarrierHandlers = useMemo(
    (): ISnapshotHandlers => ({
      onEditStart: () => {
        const snapshot: Record<string, unknown> = {};
        const currentValues = valuesRef.current;
        (
          [
            'returnCarrier',
            'returnReference',
            'returnCarrierTrackingCode',
          ] as const
        ).forEach((field) => {
          snapshot[field] = currentValues[field];
        });
        snapshotsRef.current.set('returnCarrier', snapshot);
        fileSnapshotsRef.current.set('returnCarrier', returnFileRef.current);
      },
      onCancel: () => {
        const snapshot = snapshotsRef.current.get('returnCarrier');
        if (snapshot) {
          setValues((prev) => ({ ...prev, ...snapshot }));

          const fields = [
            'returnCarrier',
            'returnReference',
            'returnCarrierTrackingCode',
          ];
          const touchedReset = fields.reduce(
            (acc, field) => {
              acc[field] = false;

              return acc;
            },
            {} as Record<string, boolean>
          );
          setTouched({ ...touched, ...touchedReset });

          snapshotsRef.current.delete('returnCarrier');
        }
        if (fileSnapshotsRef.current.has('returnCarrier')) {
          setReturnFile(fileSnapshotsRef.current.get('returnCarrier') || null);
          fileSnapshotsRef.current.delete('returnCarrier');
        }
      },
    }),
    [setValues, setTouched, setReturnFile, touched]
  );

  const returnSenderHandlers = createSnapshotHandlers(
    'returnSender',
    getAddressFields('returnSender')
  );

  const returnRecipientHandlers = createSnapshotHandlers(
    'returnRecipient',
    getAddressFields('returnRecipient')
  );

  const hasEditingCards = editingCardsCount > 0;

  return {
    handleEditingChange,
    hasEditingCards,
    hasErrorsInFields,
    getAddressFields,
    partnershipHandlers,
    boxHandlers,
    providerStationHandlers,
    typeHandlers,
    parcelHandlers,
    senderHandlers,
    recipientHandlers,
    returnCarrierHandlers,
    returnSenderHandlers,
    returnRecipientHandlers,
  };
};
